'use client';

import { getProduct, products } from '@/lib/products';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { dilaErpCopy } from '@/lib/i18n/pages/dilaerp';
import { productsCommonCopy } from '@/lib/i18n/pages/productsCommon';
import { BrowserShot, Tilt } from './shots';
import { Reveal } from './Reveal';
import { ProductHero } from './hero';
import {
  AccentGlow,
  DemoBand,
  FeatureRows,
  FloatChip,
  GalleryRail,
  NextProducts,
  ProductMarquee,
  SectionHead,
  StatGrid,
} from './blocks';

const product = getProduct('dilaerp')!;
const A = product.accent;
const IMG = '/images/products/dilaerp';

/** Real numbers, identical in every locale; labels come from the copy file. */
const GALLERY_SRC = [
  `${IMG}/invoices.webp`,
  `${IMG}/crm-deals.webp`,
  `${IMG}/hr.webp`,
  `${IMG}/coa.webp`,
];

const ERP_STATS = ['15', '300', '6,392', '26+', '~54', '71'];

/** Demo login shown on the page. Kept LTR and unchanged in every locale. */
const DEMO_LOGIN = 'demo@kurd.dilacode.com · DilaDemo!2026';

export function DilaErpPage() {
  const { locale, href } = useLocale();
  const t = pageCopy(dilaErpCopy, locale);
  const c = pageCopy(productsCommonCopy, locale);
  const cursor = { goLabel: c.cursorGo, openLabel: c.cursorOpen };
  return (
    <div className="self-styled relative text-white">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <ProductHero
        name="DilaERP"
        accent={A}
        title={t.hero.title}
        lede={t.hero.lede}
        secondary={{ label: t.hero.secondary, href: product.liveUrl, external: true }}
        liveHref={product.liveUrl}
        liveLabel={
          <>
            {t.hero.liveLabel} · <bdi dir="ltr" className="keep-latin">{product.domain}</bdi>
          </>
        }
        note={
          <bdi dir="ltr" className="keep-latin">
            {DEMO_LOGIN}
          </bdi>
        }
        breadcrumbLabel={c.breadcrumbProducts}
        productsHref={href('/products')}
        bookLabel={c.bookDemo}
        bookHref={href('/contact')}
        {...cursor}
      >
        <AccentGlow accent={A} className="start-1/4 top-1/4 h-80 w-80" opacity={0.18} />
        <Tilt>
          <BrowserShot
            src={`${IMG}/dashboard.webp`}
            alt={t.hero.imageAlt}
            url={product.domain}
            accent={A}
            aspect="aspect-[16/10]"
            priority
            sizes="(max-width: 1024px) 100vw, 55vw"
          />
        </Tilt>
        <FloatChip accent={A} className="-start-4 top-10" dot>
          {t.hero.chipPnl}
        </FloatChip>
        <FloatChip accent={A} className="-end-3 top-1/3" alt>
          {t.hero.chipPayroll}
        </FloatChip>
        <FloatChip accent={A} className="bottom-8 start-1/4">
          POS · {t.hero.chipStock} · CRM
        </FloatChip>
      </ProductHero>

      {/* ── The one marquee: product truths ──────────────────────────── */}
      <ProductMarquee accent={A} items={t.marquee} />

      {/* ── Module constellation ─────────────────────────────────────── */}
      <section aria-label={t.modules.aria} className="px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title={t.modules.title}
              lede={t.modules.lede}
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120}>
            <ul className="mt-14 flex flex-wrap items-center gap-3 md:gap-4" aria-label={t.modules.listAria}>
              {t.modules.items.map((mod, i) => (
                <li
                  key={mod}
                  className={
                    i % 4 === 0
                      ? 'rounded-full border px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] rtl:tracking-normal text-white md:text-sm'
                      : 'rounded-full border border-white/12 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] rtl:tracking-normal text-white/55'
                  }
                  style={i % 4 === 0 ? { borderColor: `${A}55`, color: A } : undefined}
                >
                  {mod}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── AI assistant (split: copy left, shot right) ──────────────── */}
      <section aria-label={t.ai.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <SectionHead
              accent={A}
              title={t.ai.title}
              lede={t.ai.lede}
            />
            <FeatureRows accent={A} className="mt-10" rows={t.ai.rows} />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-end-8 bottom-0 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/ai-ku.webp`}
              alt={t.ai.shotAlt}
              url={product.domain}
              accent={A}
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Simple Mode / POS (split: shot left, copy right) ─────────── */}
      <section aria-label={t.simple.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="relative order-2 lg:order-1 lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-start-8 top-10 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/simple-sell.webp`}
              alt={t.simple.shotAlt}
              url={`${product.domain}/simple`}
              accent={A}
              aspect="aspect-[4/3]"
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
          <Reveal className="order-1 lg:order-2 lg:col-span-5">
            <SectionHead
              accent={A}
              title={t.simple.title}
              lede={t.simple.lede}
            />
            <FeatureRows accent={A} className="mt-10" rows={t.simple.rows} />
          </Reveal>
        </div>
      </section>

      {/* ── Finance band ─────────────────────────────────────────────── */}
      <section aria-label={t.finance.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title={t.finance.title}
              lede={t.finance.lede}
            />
          </Reveal>
          <Reveal delay={120} className="relative mt-14">
            <AccentGlow accent={A} className="end-1/4 -top-8 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/pnl.webp`}
              alt={t.finance.shotAlt}
              url={`${product.domain}/reports`}
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Kurdish band (RTL display moment) ────────────────────────── */}
      <section aria-label={t.kurdish.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <span className="eyebrow" style={{ color: A }}>
              {t.kurdish.eyebrow}
            </span>
            {/* Sorani display moment: whole-string render, never split into
                letters, set in the site RTL face (Estedad). */}
            <h2
              dir="rtl"
              lang="ckb"
              className="mt-8 text-right font-[var(--font-rtl)] text-5xl font-semibold leading-[1.3] text-white sm:text-6xl md:text-7xl"
            >
              {t.kurdish.heading}
            </h2>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/65 md:text-lg">
              {t.kurdish.body}
            </p>
          </Reveal>
          <Reveal delay={140} className="relative mt-14">
            <AccentGlow accent={A} className="start-1/4 -top-8 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/dashboard-ku.webp`}
              alt={t.kurdish.shotAlt}
              url={product.domain}
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <section aria-label={t.stats.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-28 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <StatGrid
              accent={A}
              stats={ERP_STATS.map((value, i) => ({ value, label: t.stats.labels[i] }))}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Ownership + paperwork ────────────────────────────────────── */}
      <section aria-label={t.ownership.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title={t.ownership.title}
              lede={t.ownership.lede}
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120}>
            <FeatureRows accent={A} className="mt-14" rows={t.ownership.rows} />
          </Reveal>
        </div>
      </section>

      {/* ── Gallery rail ─────────────────────────────────────────────── */}
      <section aria-label={t.gallery.aria} className="border-t border-white/10 py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="mx-auto max-w-[1400px]">
            <Reveal>
              <SectionHead accent={A} title={t.gallery.title} />
            </Reveal>
          </div>
        </div>
        <Reveal delay={120}>
          <GalleryRail
            accent={A}
            className="mt-12"
            shots={GALLERY_SRC.map((src, i) => ({ src, ...t.gallery.shots[i] }))}
          />
        </Reveal>
      </section>

      {/* ── Demo + cross-sell ────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="DilaERP"
            title={t.demo.title}
            sub={t.demo.sub}
            primary={{ label: c.bookDemo, href: href('/contact') }}
            secondary={{ label: t.demo.secondary, href: product.liveUrl, external: true }}
            accent={A}
            eyebrow={c.bookDemo}
            note={
              <>
                {t.demo.liveNote}:{' '}
                <bdi dir="ltr" className="keep-latin">
                  {product.domain} · {DEMO_LOGIN}
                </bdi>
              </>
            }
            {...cursor}
          />
          <NextProducts
            className="mb-24"
            ariaLabel={c.otherProductsAria}
            viewLabel={c.cursorView}
            href={href}
            items={products
              .filter((p) => p.slug !== 'dilaerp')
              .map((p) => ({
                slug: p.slug,
                name: p.name,
                tagline: c.taglines[p.slug],
                accent: p.accent,
              }))}
          />
        </div>
      </div>
    </div>
  );
}
